/**
 * The studio's own settings inside a v65 scene file — the `host` block the
 * viewer carries without reading (`SceneHostBlock`).
 *
 * The viewer saves everything it renders; what it cannot see lives here: the
 * customer configurator, the watermark, and the On Body panel's own state.
 *
 * A host block comes back out of a FILE, so it is untrusted: `parseStudioHost`
 * rebuilds every field from scratch, field by field, and drops what does not
 * check out. Nothing from the file is spread into the studio's state as is.
 *
 * Pure: the flows that capture and apply it live in `sceneFile.ts`.
 */
import type { OnBodyAnchor, OnBodyKind, SceneHostBlock, Vec3 } from "../../../shared/api-contract";
import {
  matchGroupTargets,
  type ConfiguratorDef,
  type ConfiguratorOption,
  type ConfiguratorOptionGroup,
  type ConfiguratorVariant,
  type OptionPreset,
  type PresetCategory,
} from "../../../features/vault/configurator";
import { FINGER_NAMES, type FingerFit, type FingerName, type PlacedHand, type Vec2, type Vec3t } from "../onbody/anchorMath";
import { FIT_RANGE, WIDTH_SCALE_RANGE, type OnBodyHost, type PlaceMethod } from "../onbody/session";

export const STUDIO_HOST_APP = "jewelitt-studio";
export const STUDIO_HOST_VERSION = 1;

export interface WatermarkHost {
  on: boolean;
  text: string;
  light: boolean;
}

/** The block as the studio writes it. */
export interface StudioSceneHost extends SceneHostBlock {
  app: typeof STUDIO_HOST_APP;
  version: number;
  /** The customer configurator. Absent = this save does not speak for it
   *  (a Vault save — the Vault keeps its own); null = there was none. */
  configurator?: ConfiguratorDef | null;
  /**
   * Configurator group id → the viewer part id it targeted when saved. The
   * configurator names its parts by label and position; opening the scene on
   * another ring renames them, and the viewer's `groupMap` (saved part id →
   * live part ids) is how a slot finds its part again.
   */
  configuratorTargets?: Record<string, string>;
  watermark?: WatermarkHost | null;
  /** The On Body panel, when something is on the body; null when not. */
  onBody?: OnBodyHost | null;
}

/**
 * A host block after validation. `undefined` means "the file says nothing
 * usable about this — leave it as it is"; `null` means "the saved setup had
 * none".
 */
export interface ParsedStudioHost {
  configurator: ConfiguratorDef | null | undefined;
  configuratorTargets: Record<string, string>;
  watermark: WatermarkHost | undefined;
  onBody: OnBodyHost | null | undefined;
}

// ── primitives ──────────────────────────────────────────────────────────────

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const num = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const bool = (v: unknown): v is boolean => typeof v === "boolean";
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const int = (v: unknown, lo = 0, hi = Number.MAX_SAFE_INTEGER): number | null =>
  num(v) && Number.isInteger(v) && v >= lo && v <= hi ? v : null;

/** A non-empty string no longer than `max`, else null. */
function str(v: unknown, max = 200): string | null {
  return typeof v === "string" && v.length > 0 && v.length <= max ? v : null;
}

/** Text a person reads (may be empty), cut to `max`. */
const text = (v: unknown, max = 200): string | null => (typeof v === "string" ? v.slice(0, max) : null);

/** Data URLs carry a workspace's own files; 12 MB is far above any real one. */
const MAX_URL = 12 * 1024 * 1024;

/** An http(s) or data: URL — never `javascript:`, `blob:` (dead in another session) or a relative path. */
function safeUrl(v: unknown): string | null {
  const s = str(v, MAX_URL);
  return s && /^(https?:\/\/|data:)/i.test(s) ? s : null;
}

/** A colour for a chip: a hex code, or a plain CSS colour function / name. */
function colour(v: unknown): string | null {
  const s = str(v, 80);
  return s && (/^#[0-9a-f]{3,8}$/i.test(s) || /^[a-z]+(\([0-9.,%\s]+\))?$/i.test(s)) ? s : null;
}

const vecN = (v: unknown, n: number): boolean => Array.isArray(v) && v.length === n && v.every(num);
const vec2 = (v: unknown): Vec2 | null => (vecN(v, 2) ? ([...(v as number[])] as Vec2) : null);
const vec3t = (v: unknown): Vec3t | null => (vecN(v, 3) ? ([...(v as number[])] as Vec3t) : null);
const vec3 = (v: unknown): Vec3 | null =>
  isRecord(v) && num(v.x) && num(v.y) && num(v.z) ? { x: v.x, y: v.y, z: v.z } : null;

/** Keep the valid entries of a list (at most `max`), rebuilt by `each`. */
function list<T>(v: unknown, max: number, each: (x: unknown) => T | null): T[] | null {
  if (!Array.isArray(v)) return null;
  const out: T[] = [];
  for (const x of v.slice(0, max)) {
    const t = each(x);
    if (t !== null) out.push(t);
  }
  return out;
}

// ── configurator ────────────────────────────────────────────────────────────

const CATEGORIES: readonly PresetCategory[] = ["metal", "gem", "ceramic", "pearl", "other"];

function parsePreset(v: unknown): OptionPreset | null {
  if (!isRecord(v) || !CATEGORIES.includes(v.kind as PresetCategory)) return null;
  const url = safeUrl(v.url);
  const presetId = str(v.presetId);
  if (!url && !presetId) return null;
  const finishId = str(v.finishId);
  return {
    kind: v.kind as PresetCategory,
    ...(url ? { url } : {}),
    ...(presetId ? { presetId } : {}),
    ...(finishId ? { finishId } : {}),
    ...(v.format === "pmat" || v.format === "dmat" ? { format: v.format } : {}),
  };
}

function parseOption(v: unknown): ConfiguratorOption | null {
  if (!isRecord(v)) return null;
  const id = str(v.id);
  const name = text(v.name, 120);
  if (!id || name === null) return null;
  const metalColor = colour(v.metalColor);
  const gemPresetId = str(v.gemPresetId);
  const preset = parsePreset(v.preset);
  const thumbUrl = safeUrl(v.thumbUrl);
  return {
    id,
    name,
    swatch: colour(v.swatch) ?? "#cccccc",
    ...(metalColor ? { metalColor } : {}),
    ...(gemPresetId ? { gemPresetId } : {}),
    ...(preset ? { preset } : {}),
    ...(thumbUrl ? { thumbUrl } : {}),
    ...(num(v.priceDelta) ? { priceDelta: v.priceDelta } : {}),
  };
}

function parseGroup(v: unknown): ConfiguratorOptionGroup | null {
  if (!isRecord(v)) return null;
  const id = str(v.id);
  const label = text(v.label, 120);
  const targetGroupLabel = typeof v.targetGroupLabel === "string" ? v.targetGroupLabel.slice(0, 200) : null;
  const options = list(v.options, 200, parseOption);
  if (!id || label === null || targetGroupLabel === null || !options) return null;
  if (v.kind !== "metal" && v.kind !== "gem") return null;
  const index = int(v.targetGroupIndex, 0, 10_000);
  return {
    id,
    label,
    targetGroupLabel,
    ...(index !== null ? { targetGroupIndex: index } : {}),
    kind: v.kind,
    defaultIndex: int(v.defaultIndex, 0, 10_000) ?? 0,
    options,
  };
}

function parseVariant(v: unknown): ConfiguratorVariant | null {
  if (!isRecord(v)) return null;
  const id = str(v.id);
  const label = text(v.label, 120);
  const modelId = str(v.modelId, 64);
  if (!id || label === null || !modelId) return null;
  const thumbUrl = safeUrl(v.thumbUrl);
  return { id, label, modelId, ...(thumbUrl ? { thumbUrl } : {}) };
}

/** A configurator definition rebuilt from untrusted JSON; null when it is not one. */
export function parseConfiguratorDef(v: unknown): ConfiguratorDef | null {
  if (!isRecord(v)) return null;
  const groups = list(v.groups, 64, parseGroup);
  if (!groups) return null;
  const variants = v.variants === undefined ? null : list(v.variants, 32, parseVariant);
  const currency = typeof v.currency === "string" && /^[A-Z]{3}$/.test(v.currency) ? v.currency : null;
  return {
    groups,
    ...(num(v.basePrice) && v.basePrice >= 0 ? { basePrice: v.basePrice } : {}),
    ...(currency ? { currency } : {}),
    ...(variants ? { variants } : {}),
  };
}

/** Configurator group id → the viewer part id it targets right now (for `configuratorTargets`). */
export function configuratorTargetsOf(def: ConfiguratorDef, live: { id: string; label: string; kind: string }[]): Record<string, string> {
  return Object.fromEntries(matchGroupTargets(def.groups, live).map(({ group, target }) => [group.id, target.id]));
}

/**
 * Point each configurator group at the live part its saved part was applied
 * to (`groupMap`: saved part id → live part ids), by that part's CURRENT
 * label and position — the two things the configurator matches on. A group
 * with no recorded target, or whose part found no match, is left as saved.
 * Returns the same object when nothing moved.
 */
export function remapConfigurator(
  def: ConfiguratorDef,
  targets: Record<string, string>,
  groupMap: Record<string, string[]> | null | undefined,
  live: { id: string; label: string }[],
): ConfiguratorDef {
  if (!groupMap) return def;
  const at = new Map(live.map((g, i) => [g.id, i]));
  let moved = false;
  const groups = def.groups.map((g) => {
    const saved = targets[g.id];
    const liveId = saved ? (groupMap[saved] ?? []).find((id) => at.has(id)) : undefined;
    if (liveId === undefined) return g;
    const i = at.get(liveId) as number;
    const label = live[i].label;
    if (label === g.targetGroupLabel && i === g.targetGroupIndex) return g;
    moved = true;
    return { ...g, targetGroupLabel: label, targetGroupIndex: i };
  });
  return moved ? { ...def, groups } : def;
}

// ── On Body ─────────────────────────────────────────────────────────────────

const PLACE_METHODS: readonly PlaceMethod[] = ["library", "manual", "detected", "clicks"];
const placeMethod = (v: unknown): PlaceMethod | null => (PLACE_METHODS.includes(v as PlaceMethod) ? (v as PlaceMethod) : null);
const isFinger = (v: unknown): v is FingerName => FINGER_NAMES.includes(v as FingerName);

function parseFingerFit(v: unknown): FingerFit | null {
  if (!isRecord(v) || !num(v.width) || !num(v.shift) || typeof v.method !== "string") return null;
  const samples = list(v.widthSamples ?? [], 64, (x) => (num(x) ? x : null)) ?? [];
  return { width: v.width, widthSamples: samples, method: v.method as FingerFit["method"], shift: v.shift };
}

/** 21 landmarks, each [x, y, z], or null. */
function landmarks(v: unknown): Vec3t[] | null {
  if (!Array.isArray(v) || v.length !== 21) return null;
  const out = v.map(vec3t);
  return out.every((p): p is Vec3t => p !== null) ? out : null;
}

function parseHand(v: unknown): PlacedHand | null {
  if (!isRecord(v) || !isRecord(v.fingers)) return null;
  const fingers: PlacedHand["fingers"] = {};
  for (const f of FINGER_NAMES) {
    const fit = v.fingers[f] === undefined ? null : parseFingerFit(v.fingers[f]);
    if (fit) fingers[f] = fit;
  }
  const image = v.image === undefined ? null : landmarks(v.image);
  const world = v.world === undefined ? null : landmarks(v.world);
  if ((v.image !== undefined && !image) || (v.world !== undefined && !world)) return null;
  let joints: PlacedHand["joints"] | undefined;
  if (isRecord(v.joints)) {
    joints = {};
    for (const f of FINGER_NAMES) {
      const j = v.joints[f];
      const a = Array.isArray(j) && j.length === 2 ? vec2(j[0]) : null;
      const b = Array.isArray(j) && j.length === 2 ? vec2(j[1]) : null;
      if (a && b) joints[f] = [a, b];
    }
  }
  // A hand with no landmarks and no bones has nothing to put a ring on.
  if (!image && !joints) return null;
  return {
    handedness: typeof v.handedness === "string" ? v.handedness.slice(0, 16) : null,
    score: num(v.score) ? v.score : null,
    ...(image ? { image } : {}),
    ...(world ? { world } : {}),
    ...(joints ? { joints } : {}),
    fingers,
  };
}

function parseAnchor(v: unknown): OnBodyAnchor | null {
  if (!isRecord(v) || !num(v.width)) return null;
  const p0 = vec2(v.p0);
  const p1 = vec2(v.p1);
  const center = vec2(v.center);
  if (!p0 || !p1 || !center) return null;
  const axis3 = vec3t(v.axis3);
  const axisWorld = vec3t(v.axisWorld);
  const dorsal = vec3t(v.dorsal);
  return {
    p0,
    p1,
    center,
    width: v.width,
    ...(axis3 ? { axis3 } : {}),
    ...(num(v.along) ? { along: v.along } : {}),
    ...(axisWorld ? { axisWorld } : {}),
    ...(dorsal ? { dorsal } : {}),
  };
}

/** The On Body panel's saved state rebuilt from untrusted JSON; null when it is not one. */
export function parseOnBodyHost(v: unknown): OnBodyHost | null {
  if (!isRecord(v) || (v.kind !== "finger" && v.kind !== "neck") || !isRecord(v.photo)) return null;
  const p = v.photo;
  const key = str(p.key, 400);
  const label = text(p.label, 200);
  const url = p.url === null ? null : safeUrl(p.url);
  const hands = list(p.hands, 8, parseHand);
  const handsBy = placeMethod(p.handsBy);
  const neckBy = placeMethod(p.neckBy);
  const neck = p.neck === null ? null : parseAnchor(p.neck);
  if (!key || label === null || (p.url !== null && !url) || !hands || !handsBy || !neckBy || (p.neck !== null && !neck)) return null;
  if (!num(p.w) || !num(p.h) || p.w <= 0 || p.h <= 0 || !bool(p.fromFile)) return null;
  if (!isFinger(v.finger) || !isRecord(v.fit) || !num(v.fit.finger) || !num(v.fit.neck)) return null;
  const fit: Record<OnBodyKind, number> = {
    finger: clamp(v.fit.finger, FIT_RANGE.finger.min, FIT_RANGE.finger.max),
    neck: clamp(v.fit.neck, FIT_RANGE.neck.min, FIT_RANGE.neck.max),
  };
  return {
    kind: v.kind,
    photo: {
      key,
      label,
      url,
      fromFile: p.fromFile,
      w: p.w,
      h: p.h,
      hands,
      handsBy,
      neck,
      neckBy,
      ...(p.widthUnmeasured === true ? { widthUnmeasured: true } : {}),
    },
    handIndex: Math.min(int(v.handIndex, 0, 7) ?? 0, Math.max(0, hands.length - 1)),
    finger: v.finger,
    along: num(v.along) ? clamp(v.along, 0, 2) : 0.65,
    fit,
    headToCamera: v.headToCamera === true,
    contactShadow: num(v.contactShadow) ? clamp(v.contactShadow, 0, 1) : 0.5,
    widthScale: num(v.widthScale) ? clamp(v.widthScale, WIDTH_SCALE_RANGE.min, WIDTH_SCALE_RANGE.max) : 1,
    baseScale: num(v.baseScale) && v.baseScale > 0 ? v.baseScale : 1,
    pivotLocal: vec3(v.pivotLocal),
    posStep: num(v.posStep) && v.posStep > 0 ? v.posStep : 0.01,
  };
}

// ── the block ───────────────────────────────────────────────────────────────

function parseWatermark(v: unknown): WatermarkHost | undefined {
  if (!isRecord(v) || !bool(v.on) || !bool(v.light)) return undefined;
  const t = text(v.text, 200);
  return t === null ? undefined : { on: v.on, text: t, light: v.light };
}

/**
 * Validate a host block read back from a scene. Null when it is not the
 * studio's (another app's block, junk, a newer version than this build knows
 * how to read); otherwise every field rebuilt or reported as `undefined`.
 */
export function parseStudioHost(raw: unknown): ParsedStudioHost | null {
  if (!isRecord(raw) || raw.app !== STUDIO_HOST_APP) return null;
  const version = int(raw.version, 1, 1000);
  if (version === null || version > STUDIO_HOST_VERSION) return null;
  const configurator = raw.configurator === null ? null : (parseConfiguratorDef(raw.configurator) ?? undefined);
  const targets: Record<string, string> = {};
  if (configurator && isRecord(raw.configuratorTargets)) {
    const ids = new Set(configurator.groups.map((g) => g.id));
    for (const [k, v] of Object.entries(raw.configuratorTargets)) {
      const id = str(v, 200);
      if (ids.has(k) && id) targets[k] = id;
    }
  }
  return {
    configurator,
    configuratorTargets: targets,
    watermark: parseWatermark(raw.watermark),
    onBody: raw.onBody === null ? null : (parseOnBodyHost(raw.onBody) ?? undefined),
  };
}
